// useMentions — unseen-mentions badge for the shell header (Epic 4 · S4.3).
//
// fn_my_mentions(p_unseen_only) returns the mentions waiting on this person; we
// poll the unseen set every 60s (00_CONTRACT §13) for the header badge. Clicking a
// mention calls fn_mention_seen(id) and opens the inbox drawer at the mention's
// thread (the caller wires that — this hook just supplies the data + markSeen).
//
// Errors degrade to null (badge hidden), never to zero — a null count means
// "couldn't load", a zero means "nobody mentioned you", and conflating them is a
// lie (00_CONTRACT §6: null ≠ empty). fn_my_mentions is confirmed to EXIST on the
// live Supabase (docs/CLIENTHUB-RPC-GAPS.md §1); the shape needs a signed-in probe.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMyMentions, markMentionSeen } from '../api/shell';

const POLL_MS = 60000;

export function useMentions() {
  const [mentions, setMentions] = useState([]);
  const [unseen, setUnseen] = useState(null); // null = couldn't load (badge hidden)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getMyMentions(true);
      const list = Array.isArray(res?.data) ? res.data : [];
      setMentions(list);
      setUnseen(list.length);
      setError(null);
    } catch (err) {
      // Degrade to null (badge hidden), never zero.
      setUnseen(null);
      setError(err?.message || "Couldn't load mentions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [refresh]);

  const markSeen = useCallback(async (id = null) => {
    try {
      await markMentionSeen(id);
      setMentions((prev) => (id == null ? [] : prev.filter((m) => m.id !== id)));
      setUnseen((prev) => (prev == null ? null : Math.max(0, prev - 1)));
    } catch {
      /* best-effort; the next poll resyncs */
    }
  }, []);

  return { unseen, mentions, loading, error, markSeen, refresh };
}

export default useMentions;
